/* eslint-disable consistent-return */
import { bootstrap, ContentBlockRenderer } from '@plugins/next-cms-core';

import DefaultTemplate from '@components/templates/DefaultTemplate';
import Error from 'next/error';
import Head from 'next/head';
import PageInsetTop from '@components/atoms/PageInsetTop';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import Banner from '@components/molecules/Banner';
import { getAppData } from '@lib/appInitialState';
import { components } from '../../cmsconfig';

// eslint-disable-next-line no-unused-vars
export default function JobPostingItem({
  appData,
  page,
}) {
  let isBannerRendered = false;

  if (!page) {
    return (
      <Error statusCode={404} title="Stellenanzeige nicht gefunden" />
    );
  }

  if (!page.publishedAt) {
    return (
      <Error statusCode={410} title="Stellenausschreibung nicht mehr verfügbar" />
    );
  }

  const {
    metaPageTitle,
    metaPageDescription,
    isTopContentOffset,
  } = page;
  const componentData = {
    id: page.id,
    blocks: page.content ?? [],
  };

  const pageTitle = `${metaPageTitle ?? ''}`;

  return (
    <>
      <Head>
        <title>
          {pageTitle}
        </title>
        <meta content={metaPageDescription} name="description" />
        <meta content={pageTitle} property="og:title" />
      </Head>
      <DefaultTemplate>
        {isTopContentOffset && <PageInsetTop />}
        <ContentBlockRenderer
          components={components}
          context={{ page }}
          data={componentData}
          appData={appData}
          // eslint-disable-next-line react/no-unstable-nested-components
          preComponentRender={(component) => {
            // eslint-disable-next-line no-underscore-dangle
            if (!isBannerRendered && !PRE_RENDER_SKIP_COMPONENTS.includes(component.typeName)) {
              isBannerRendered = true;

              return (
                <Banner appData={appData} />
              );
            }
          }}
        />
      </DefaultTemplate>
    </>
  );
}

JobPostingItem.propTypes = {
  page: PropTypes.object,
};

export async function getServerSideProps(ctx) {
  const {
    query,
    req,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const { slug } = query;

  const {
    data,
    error,
  } = await graphQlClient.query({
    query: FETCH_PAGE,
    variables: {
      slug,
      locale,
      localeStr: locale,
    },
  });

  if (error) {
    throw error;
  }

  const resolvedPage = data.resolveJobPostingPageBySlug;
  const redirectUrl = resolvedPage?.redirectUrl?.trim();

  if (resolvedPage?.publishedAt && redirectUrl) {
    let canRedirect = false;
    try {
      const dest = new URL(redirectUrl);
      const currentHost = req?.headers?.host;
      const currentPath = (req?.url ?? '').split('?')[0];
      canRedirect = !(dest.host === currentHost && dest.pathname === currentPath);
    } catch {
      canRedirect = false;
    }
    if (canRedirect) {
      return {
        redirect: {
          destination: redirectUrl,
          statusCode: 301,
        },
      };
    }
  }

  if (!resolvedPage && res) {
    res.statusCode = 404;
  } else if (!resolvedPage?.publishedAt && res) {
    res.statusCode = 410;
  }

  if (res && data.cacheState.isEnabled) {
    res.setHeader('Cache-Control', 'public, max-age=3600');
  }

  const statusCode = res?.statusCode ?? 200;

  return {
    props: {
      ...appData,
      statusCode: res?.statusCode ?? 200,
      slug,
      page: data.resolveJobPostingPageBySlug,
      pageBottomConfig: {
        bottomInfoText: data.resolveJobPostingPageBySlug?.bottomInfoText ?? null,
      },
      isGlobalNoticeHidden: Boolean(data.resolveJobPostingPageBySlug?.isGlobalNoticeHidden),
      canonicalUrl: data.resolveJobPostingPageBySlug?.metaCanonicalUrl ?? null,
    },
  };
}

const { graphQlFragments } = bootstrap({
  components,
});

const PRE_RENDER_SKIP_COMPONENTS = [
  'ComponentContentPageHeader',
  'ComponentContentCarSearchFilter',
  'ComponentContentModelRequestForm',
];

const FETCH_PAGE = gql`
  query GetPageBySlug($slug: String!, $locale: I18NLocaleCode!, $localeStr: String!) {
    resolveJobPostingPageBySlug(locale: $locale, slug: $slug) {
      publishedAt
      publishDate
      name
      isTopContentOffset
      bottomInfoText
      metaPageTitle
      metaPageDescription
      metaCanonicalUrl
      redirectUrl
      content {
        ${graphQlFragments}
      }
    }
    cacheState {
      isEnabled
    }
  }
`;
